import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, ChevronDown, RefreshCw, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const money = (v) => `${v >= 0 ? "+" : "-"}$${Math.abs(v).toFixed(2)}`;

const EditInput = ({ value, onChange, testid, disabled, label }) => (
  <div className="flex-1 min-w-[100px]">
    <span className="block text-[10px] uppercase tracking-[0.14em] text-zinc-400 font-semibold mb-1">
      {label}
    </span>
    <input
      data-testid={testid}
      disabled={disabled}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder="—"
      inputMode="decimal"
      className="w-full rounded-sm bg-zinc-900/80 border border-zinc-700 px-2.5 py-2.5 font-mono text-sm text-zinc-100 focus:border-emerald-500 focus:outline-none transition-colors duration-200 disabled:opacity-40"
    />
  </div>
);

export const PositionCards = ({
  positions,
  onRefresh,
  refreshing,
  updatedAt,
  live,
  onPatch,
  onDrop,
  highlightTicket,
}) => {
  const [partial, setPartial] = useState({});
  const [edits, setEdits] = useState({});
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [flash, setFlash] = useState({});
  const prevPnl = useRef({});

  // A winner sits solid green; only a losing trade pulses, and only in red. The card's colour is
  // decided by the sign of the floating P&L alone, never by the direction of the last tick.
  useEffect(() => {
    const hits = {};
    for (const p of positions) {
      const was = prevPnl.current[p.ticket];
      if (was !== undefined && was !== p.profit && p.profit < 0) hits[p.ticket] = "down";
      prevPnl.current[p.ticket] = p.profit;
    }
    if (!Object.keys(hits).length) return;
    setFlash((s) => ({ ...s, ...hits }));
    const id = setTimeout(
      () =>
        setFlash((s) => {
          const next = { ...s };
          for (const k of Object.keys(hits)) delete next[k];
          return next;
        }),
      300
    );
    return () => clearTimeout(id);
  }, [positions]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, []);
  const age = updatedAt ? Math.max(0, (now - updatedAt) / 1000) : null;

  const field = (p, key) => {
    const e = edits[p.ticket]?.[key];
    if (e !== undefined) return e;
    const live = key === "stop_loss" ? p.sl : key === "tp1" ? p.tp1 : p.tp2 || p.tp;
    return live ?? "";
  };
  const setField = (ticket, key) => (v) =>
    setEdits((s) => ({ ...s, [ticket]: { ...s[ticket], [key]: v } }));
  const dirty = (ticket) => Object.keys(edits[ticket] || {}).length > 0;

  const saveLevels = async (p) => {
    const body = {};
    const e = edits[p.ticket] || {};
    const live = { stop_loss: p.sl, tp1: p.tp1, tp2: p.tp2 || p.tp };
    const same = (a, b) =>
      Math.round((parseFloat(a) || 0) * 1e6) === Math.round((parseFloat(b) || 0) * 1e6);
    for (const key of ["stop_loss", "tp1", "tp2"]) {
      const v = e[key];
      if (v === undefined || v === "") continue;
      if (Number.isNaN(parseFloat(v))) continue;
      if (same(v, live[key])) continue;
      body[key] = parseFloat(v);
    }
    if (!Object.keys(body).length) {
      toast.error("No changes detected");
      return;
    }
    setBusy(p.ticket);
    try {
      await api.put(`/positions/${p.ticket}/levels`, body);
      // Show it on the row at once; the next socket frame confirms it.
      onPatch?.(p.ticket, {
        ...(body.stop_loss !== undefined ? { sl: body.stop_loss } : {}),
        ...(body.tp1 !== undefined ? { tp1: body.tp1 } : {}),
        ...(body.tp2 !== undefined ? { tp2: body.tp2 } : {}),
      });
      toast.success(`Levels updated on ${p.symbol}`);
      setEdits((s) => ({ ...s, [p.ticket]: {} }));
      onRefresh?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(null);
    }
  };

  const close = async (ticket, volume) => {
    setBusy(ticket);
    try {
      const { data } = await api.post(`/positions/${ticket}/close`, { volume: volume ?? null });
      // The row goes (or shrinks) immediately — no reload round trip to MT5 to find that out.
      if (data.remaining_volume > 0) onPatch?.(ticket, { volume: data.remaining_volume });
      else onDrop?.(ticket);
      toast.success(`Closed ${data.closed_volume} lots · P&L ${money(data.profit)}`);
      setPartial((s) => ({ ...s, [ticket]: "" }));
      onRefresh?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const toggleTrail = async (p) => {
    setBusy(p.ticket);
    try {
      await api.put(`/positions/${p.ticket}/trailing`, { enabled: !p.trail_on });
      onPatch?.(p.ticket, { trail_on: !p.trail_on });
      toast.success(
        !p.trail_on
          ? `Trailing SL ON for ${p.symbol} (${p.trail_pct}% of first-SL distance)`
          : `Trailing SL OFF for ${p.symbol} — SL stays where it is`
      );
      onRefresh?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-3" data-testid="positions-panel">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-heading font-semibold tracking-tight text-zinc-100">
          Open Positions{" "}
          <span className="text-zinc-400 font-mono text-xs">({positions.length})</span>
          {age !== null && (
            <span
              data-testid="positions-updated"
              className="ml-2 font-mono text-[10px] uppercase tracking-[0.1em] text-zinc-400"
            >
              <span className="text-emerald-400">●</span> {live ? "live" : "polling"} ·
              updated {age.toFixed(1)}s ago
            </span>
          )}
        </h3>
        <button
          data-testid="refresh-positions-btn"
          onClick={onRefresh}
          className="flex items-center gap-2 border border-zinc-700 text-zinc-200 hover:bg-zinc-800 hover:text-white px-3 py-2 rounded-sm text-xs uppercase tracking-[0.1em] font-semibold transition-colors duration-200"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {positions.length === 0 && (
        <p
          className="border border-zinc-800 bg-zinc-900/40 rounded-sm px-4 py-12 text-center text-sm text-zinc-400"
          data-testid="no-positions"
        >
          No open positions.
        </p>
      )}

      {positions.map((p) => {
        const expanded = open === p.ticket;
        const win = p.profit > 0;
        const loss = p.profit < 0;
        const isNew = highlightTicket === p.ticket;
        return (
          <div
            key={p.ticket}
            data-testid={`position-row-${p.ticket}`}
            className={`rounded-sm border transition-shadow duration-300 ${
              isNew
                ? "border-emerald-400 shadow-[0_0_0_3px_rgba(16,185,129,0.35),0_0_28px_rgba(16,185,129,0.55)]"
                : win
                ? "border-emerald-700"
                : loss
                ? flash[p.ticket] === "down"
                  ? "border-red-500 shadow-[0_0_0_2px_rgba(239,68,68,0.45),0_0_22px_rgba(239,68,68,0.5)]"
                  : "border-red-800/70"
                : "border-zinc-800"
            } ${
              win
                ? "bg-emerald-500/[0.16]"
                : loss
                ? "bg-red-500/[0.13]"
                : "bg-zinc-900/40"
            }`}
          >
            <div className="p-3 sm:p-4 flex flex-col gap-2.5">
              <button
                type="button"
                data-testid={`position-card-${p.ticket}`}
                onClick={() => setOpen(expanded ? null : p.ticket)}
                className="w-full min-w-0 text-left flex flex-col gap-2.5"
              >
                {/* Row 1 — instrument · side · lots */}
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-heading font-bold text-base sm:text-lg tracking-tight text-zinc-50 truncate">
                    {p.symbol}
                  </span>
                  <span
                    className={`px-1.5 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wide shrink-0 ${
                      p.side === "buy"
                        ? "bg-emerald-500/25 text-emerald-200"
                        : "bg-red-500/25 text-red-200"
                    }`}
                  >
                    {p.side}
                  </span>
                  <span className="font-mono text-xs text-zinc-200 shrink-0">{p.volume} lots</span>
                  <span
                    data-testid={`source-${p.ticket}`}
                    className={`px-1.5 py-0.5 rounded-full text-[9px] uppercase font-semibold shrink-0 ${
                      p.source === "External"
                        ? "bg-amber-500/20 text-amber-200"
                        : "bg-emerald-500/15 text-emerald-200"
                    }`}
                  >
                    {p.source === "External" ? "External" : "Strict RR"}
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 text-zinc-400 ml-auto shrink-0 transition-transform duration-200 ${
                      expanded ? "rotate-180" : ""
                    }`}
                  />
                </div>

                {/* Row 2 — P&L (large, centered) + current price */}
                <div className="text-center">
                  <p
                    data-testid={`pnl-${p.ticket}`}
                    data-flash={flash[p.ticket] || "none"}
                    className={`font-mono font-bold text-2xl sm:text-3xl tracking-tight leading-none transition-colors duration-300 ${
                      flash[p.ticket] === "down"
                        ? "text-red-300 [text-shadow:0_0_14px_rgba(239,68,68,0.85)]"
                        : win
                        ? "text-emerald-300"
                        : loss
                        ? "text-red-300"
                        : "text-zinc-100"
                    }`}
                  >
                    {money(p.profit)}
                  </p>
                  <p className="font-mono text-[11px] text-zinc-400 mt-1">
                    now {p.price_current ?? "—"}
                  </p>
                </div>

                {/* Row 3 — entry · SL · allowance chips */}
                <div className="flex items-center justify-center gap-1.5 flex-wrap font-mono text-[11px]">
                  <span className="px-2 py-0.5 rounded-full bg-zinc-800/80 text-zinc-200">
                    entry {p.price_open ?? "—"}
                  </span>
                  <span
                    className="px-2 py-0.5 rounded-full bg-zinc-800/80 text-red-300"
                    title={`First SL ${p.first_sl ?? "—"} — locked at trade open`}
                  >
                    SL {p.sl ?? "—"}
                  </span>
                  <span data-testid={`first-sl-${p.ticket}`} className="hidden">
                    {p.first_sl ?? "—"}
                  </span>
                  {p.allowance_left !== null && p.allowance_left !== undefined && (
                    <span
                      data-testid={`allowance-left-${p.ticket}`}
                      className="px-2 py-0.5 rounded-full bg-zinc-800/80 text-zinc-300"
                      title="Allowance remaining before the maximum SL boundary"
                    >
                      {p.allowance_left}% left
                    </span>
                  )}
                </div>

                {/* Row 4 — TP1 · status · final target */}
                <div className="flex items-center justify-center gap-1.5 flex-wrap font-mono text-[11px]">
                  <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300">
                    TP1 {p.tp1 ?? "—"}
                  </span>
                  {p.tp1 && (
                    <span
                      data-testid={`tp1-status-${p.ticket}`}
                      className={`px-2 py-0.5 rounded-full text-[9px] uppercase font-semibold tracking-wide ${
                        p.tp1_hit
                          ? "bg-emerald-500/25 text-emerald-200"
                          : "bg-amber-500/20 text-amber-200"
                      }`}
                    >
                      {p.tp1_hit ? "reached" : "pending"}
                    </span>
                  )}
                  <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300">
                    target {p.tp2 || p.tp || "—"}
                  </span>
                </div>
              </button>

              {/* Bottom action row — compact pills */}
              <div className="flex items-center gap-2 sm:justify-end">
                <button
                  type="button"
                  data-testid={`trail-toggle-${p.ticket}`}
                  disabled={busy === p.ticket || p.source === "External"}
                  onClick={() => toggleTrail(p)}
                  title="Trailing stop loss"
                  className={`flex-1 sm:flex-none sm:w-32 h-9 rounded-full border text-[10px] uppercase font-bold tracking-wide transition-colors duration-200 disabled:opacity-40 ${
                    p.trail_on
                      ? "border-emerald-500 bg-emerald-500/20 text-emerald-200"
                      : "border-zinc-600 text-zinc-300 hover:bg-zinc-800"
                  }`}
                >
                  Trail {p.trail_on ? "ON" : "OFF"}
                </button>
                <button
                  data-testid={`close-full-btn-${p.ticket}`}
                  disabled={busy === p.ticket}
                  onClick={() => close(p.ticket, null)}
                  className="flex-1 sm:flex-none sm:w-32 h-9 inline-flex items-center justify-center gap-1.5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[10px] uppercase font-bold tracking-wide transition-colors duration-200 active:scale-95 disabled:opacity-40"
                >
                  <X className="h-3.5 w-3.5" /> Close
                </button>
              </div>
            </div>

            {expanded && (
              <div
                data-testid={`position-edit-${p.ticket}`}
                className="px-4 sm:px-5 pb-4 pt-1 bg-black/40 border-t border-zinc-800/70 space-y-3"
              >
                <div className="flex gap-2 flex-wrap pt-3">
                  <div className="flex-1 min-w-[100px]">
                    <span className="block text-[10px] uppercase tracking-[0.14em] text-zinc-400 font-semibold mb-1">
                      First SL · locked
                    </span>
                    <input
                      data-testid={`first-sl-input-${p.ticket}`}
                      value={p.first_sl ?? ""}
                      disabled
                      className="w-full rounded-sm bg-zinc-900/40 border border-zinc-800 px-2.5 py-2.5 font-mono text-sm text-zinc-500 cursor-not-allowed"
                    />
                  </div>
                  <EditInput
                    label="New SL"
                    testid={`sl-edit-${p.ticket}`}
                    value={field(p, "stop_loss")}
                    onChange={setField(p.ticket, "stop_loss")}
                  />
                  <EditInput
                    label="TP1"
                    testid={`tp1-edit-${p.ticket}`}
                    value={field(p, "tp1")}
                    onChange={setField(p.ticket, "tp1")}
                    disabled={p.tp1_hit}
                  />
                  <EditInput
                    label="Final target"
                    testid={`tp2-edit-${p.ticket}`}
                    value={field(p, "tp2")}
                    onChange={setField(p.ticket, "tp2")}
                  />
                </div>
                {p.sl_min !== null && p.sl_min !== undefined && (
                  <p className="font-mono text-[11px] text-zinc-400" data-testid={`sl-range-${p.ticket}`}>
                    Allowed New SL: {Number(p.sl_min).toFixed(5)} … {Number(p.sl_max).toFixed(5)}{" "}
                    ({p.allowance_pct}% allowance) · changing TP1 re-derives the final target
                  </p>
                )}
                <div className="flex items-end gap-2 flex-wrap">
                  {dirty(p.ticket) && (
                    <>
                      <button
                        data-testid={`save-levels-btn-${p.ticket}`}
                        disabled={busy === p.ticket}
                        onClick={() => saveLevels(p)}
                        className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2.5 rounded-sm text-xs uppercase font-bold tracking-wide transition-colors duration-200 disabled:opacity-40"
                      >
                        <Check className="h-3.5 w-3.5" /> Save
                      </button>
                      <button
                        data-testid={`cancel-levels-btn-${p.ticket}`}
                        onClick={() => setEdits((s) => ({ ...s, [p.ticket]: {} }))}
                        className="border border-zinc-700 text-zinc-200 hover:bg-zinc-800 hover:text-white px-4 py-2.5 rounded-sm text-xs uppercase font-semibold transition-colors duration-200"
                      >
                        Undo
                      </button>
                    </>
                  )}
                  <div className="flex-1" />
                  <input
                    data-testid={`partial-volume-input-${p.ticket}`}
                    value={partial[p.ticket] || ""}
                    onChange={(e) => setPartial((s) => ({ ...s, [p.ticket]: e.target.value }))}
                    placeholder="lots"
                    inputMode="decimal"
                    className="w-24 rounded-sm bg-zinc-900/80 border border-zinc-700 px-2.5 py-2.5 font-mono text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-emerald-500 focus:outline-none transition-colors duration-200"
                  />
                  <button
                    data-testid={`partial-close-btn-${p.ticket}`}
                    disabled={busy === p.ticket || !partial[p.ticket]}
                    onClick={() => close(p.ticket, parseFloat(partial[p.ticket]))}
                    className="border border-zinc-700 text-zinc-200 hover:bg-zinc-800 hover:text-white px-4 py-2.5 rounded-sm text-xs uppercase font-semibold tracking-wide transition-colors duration-200 disabled:opacity-40"
                  >
                    Partial close
                  </button>
                </div>
                <p className="text-xs text-zinc-400">
                  New SL must stay inside the allowed band; anything outside is snapped to the
                  nearest boundary. TP1 and the final target are free to move.
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
